// src/pages/TimesheetPage.tsx — Sign-In System (Build 1): Sign In Timesheet.
//
// One route, shape forks on me.isSsoAdmin (gotcha: don't ship two routes).
//   - Non-admin (and admin "View as me"): own shift history (State 9 layout).
//   - SSO admin: desktop-first master-detail (State 17) — left list, right
//     per-person timesheet. <1024px collapses to a single list with a
//     full-screen drill-down. XLSX export from the toolbar.
//
// Live rows (signed_off_at NULL) tint sage and sort to the top (server-side).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Card, Section, TopBar, PermissionLoading } from "../components/ui";

interface Shift {
  id: number;
  userId: number;
  userName: string;
  userEmail: string | null;
  roleDisplayName: string;
  locationDisplayName: string;
  signedInAt: string;
  signedOffAt: string | null;
  durationMinutes: number;
  isLive: boolean;
  wasAutoSignoff: boolean;
}
interface ListResponse { shifts: Shift[]; total: number; totalDurationMinutes: number; hasMore: boolean }

type DayFilterId = "today" | "yesterday" | "week" | "all";
const DAY_FILTERS: ReadonlyArray<{ id: DayFilterId; label: string }> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "week", label: "This week" },
  { id: "all", label: "All" },
];

function londonYmd(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
function rangeFor(id: DayFilterId): { from: string | null; to: string | null } {
  const now = new Date();
  if (id === "all") return { from: null, to: null };
  if (id === "today") { const t = londonYmd(now); return { from: t, to: t }; }
  if (id === "yesterday") { const y = londonYmd(new Date(now.getTime() - 86400000)); return { from: y, to: y }; }
  // week — Monday (London) to today
  const dow = (now.getDay() + 6) % 7; // 0=Mon
  const monday = new Date(now.getTime() - dow * 86400000);
  return { from: londonYmd(monday), to: londonYmd(now) };
}
function fmtDuration(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { timeZone: "Europe/London", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
}

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return desktop;
}

export function TimesheetPage() {
  const { me } = useCurrentUser();
  const navigate = useNavigate();
  // Who may see the all-users view: SSO admins + HR admins. Everyone else
  // (incl. sign-in-enabled users) gets their own history only. Mirrors the
  // server gate on GET /api/timesheet + /export.
  const isAdmin = !!me?.isSsoAdmin || !!me?.isHrAdmin;
  const desktop = useIsDesktop();

  const [viewAsMe, setViewAsMe] = useState(false);
  const [day, setDay] = useState<DayFilterId>("today");
  const [search, setSearch] = useState("");
  const [resp, setResp] = useState<ListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const adminAll = isAdmin && !viewAsMe;

  const load = useCallback(async () => {
    setErr(null);
    const { from, to } = rangeFor(day);
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const path = adminAll ? `/api/timesheet?${qs}` : `/api/timesheet/me?${qs}`;
    try {
      setResp(await apiFetch<ListResponse>(path));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't load the timesheet.");
    }
  }, [adminAll, day]);

  useEffect(() => { if (me) void load(); }, [me, load]);

  const filtered = useMemo(() => {
    const rows = resp?.shifts ?? [];
    if (!adminAll || !search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => r.userName.toLowerCase().includes(q) || r.roleDisplayName.toLowerCase().includes(q) || (r.userEmail ?? "").toLowerCase().includes(q));
  }, [resp, adminAll, search]);

  // Admin master-detail selection.
  const [selected, setSelected] = useState<{ userId: number; name: string } | null>(null);

  if (!me) return <PermissionLoading title="Sign In Timesheet" />;

  const exportUrl = () => {
    const { from, to } = rangeFor(day);
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    return `/api/timesheet/export?${qs}`;
  };

  return (
    <div>
      <TopBar title="Sign In Timesheet" sub={adminAll ? "All sign-in records" : "Your shifts"} onBack={() => navigate("/apps")} />
      <div style={{ padding: "14px 16px 24px" }}>
        {/* Toolbar */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 14 }}>
          {DAY_FILTERS.map((f) => (
            <button key={f.id} onClick={() => setDay(f.id)} style={pill(day === f.id)}>{f.label}</button>
          ))}
          <div style={{ flex: 1 }} />
          {isAdmin && (
            <button onClick={() => { setViewAsMe((v) => !v); setSelected(null); }} style={pill(false)}>
              {viewAsMe ? "View all users" : "View as me"}
            </button>
          )}
          {adminAll && (
            <a href={exportUrl()} style={{ ...pill(false), textDecoration: "none", color: C.brassDk, borderColor: C.brassLt }}>Export .xlsx</a>
          )}
        </div>

        {adminAll && (
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, role or email" style={{ width: "100%", boxSizing: "border-box", marginBottom: 14, fontFamily: F.body, fontSize: 14, border: `1px solid ${C.hairline}`, borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
        )}

        {err && <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{err}</div></Card>}

        {!err && (
          adminAll && desktop ? (
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16, alignItems: "start" }}>
              <ShiftList rows={filtered} admin onSelect={(r) => setSelected({ userId: r.userId, name: r.userName })} selectedUserId={selected?.userId ?? null} totals={resp} />
              <PersonPane selected={selected} day={day} navigate={navigate} />
            </div>
          ) : (
            <>
              <ShiftList rows={filtered} admin={adminAll} onSelect={adminAll ? (r) => setSelected({ userId: r.userId, name: r.userName }) : undefined} selectedUserId={null} totals={resp} />
              {adminAll && selected && (
                <DrilldownSheet selected={selected} day={day} navigate={navigate} onClose={() => setSelected(null)} />
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ShiftList({ rows, admin, onSelect, selectedUserId, totals }: { rows: Shift[]; admin: boolean; onSelect?: (r: Shift) => void; selectedUserId: number | null; totals: ListResponse | null }) {
  return (
    <div>
      <Card padded={false}>
        {rows.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute }}>No shifts in this range.</div>
        )}
        {rows.map((r, i) => (
          <div
            key={r.id}
            onClick={onSelect ? () => onSelect(r) : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
              borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}`,
              background: r.isLive ? C.sageBg : selectedUserId === r.userId ? C.paperWarm : "transparent",
              cursor: onSelect ? "pointer" : "default",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {admin && <div style={{ fontFamily: F.body, fontWeight: 800, fontSize: 14, color: C.ink }}>{r.userName}</div>}
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.inkSoft, letterSpacing: 0.3, textTransform: "uppercase", marginTop: admin ? 2 : 0 }}>
                {r.roleDisplayName} · {r.locationDisplayName}
              </div>
              <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkMute, marginTop: 2 }}>{fmtWhen(r.signedInAt)}{r.signedOffAt ? ` → ${fmtWhen(r.signedOffAt)}` : ""}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: F.mono, fontWeight: 700, fontSize: 14, color: C.ink }}>{fmtDuration(r.durationMinutes)}</div>
              <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: r.isLive ? C.sageDk : C.inkMute, marginTop: 2 }}>
                {r.isLive ? "Live" : r.wasAutoSignoff ? "Auto" : "Closed"}
              </div>
            </div>
          </div>
        ))}
        {totals && (
          <div style={{ padding: "10px 14px", borderTop: `1.5px solid ${C.hairline}`, display: "flex", justifyContent: "space-between", fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.inkSoft }}>
            <span>{totals.total} shift{totals.total === 1 ? "" : "s"}</span>
            <span>{fmtDuration(totals.totalDurationMinutes)} total</span>
          </div>
        )}
      </Card>
    </div>
  );
}

function PersonPane({ selected, day, navigate }: { selected: { userId: number; name: string } | null; day: DayFilterId; navigate: (p: string) => void }) {
  const [rows, setRows] = useState<ListResponse | null>(null);
  useEffect(() => {
    if (!selected) { setRows(null); return; }
    const { from, to } = rangeFor(day);
    const qs = new URLSearchParams({ user: String(selected.userId) });
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    apiFetch<ListResponse>(`/api/timesheet?${qs}`).then(setRows).catch(() => setRows(null));
  }, [selected, day]);

  if (!selected) {
    return <Card><div style={{ padding: 12, textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute }}>Select a row to see that person's timesheet.</div></Card>;
  }
  return (
    <div>
      <Section title={selected.name} sub="Individual timesheet" right={
        <button onClick={() => navigate("/access/users")} style={{ background: "none", border: "none", color: C.brassDk, fontFamily: F.body, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Open profile →</button>
      } />
      <ShiftList rows={rows?.shifts ?? []} admin={false} selectedUserId={null} totals={rows} />
    </div>
  );
}

function DrilldownSheet({ selected, day, navigate, onClose }: { selected: { userId: number; name: string }; day: DayFilterId; navigate: (p: string) => void; onClose: () => void }) {
  return (
    <div role="dialog" aria-label={`Timesheet for ${selected.name}`} style={{ position: "fixed", inset: 0, background: C.paper, zIndex: 200, overflowY: "auto" }}>
      <TopBar title={selected.name} sub="Individual timesheet" />
      <div style={{ padding: "14px 16px 24px" }}>
        <button onClick={onClose} style={{ ...pill(false), marginBottom: 14 }}>← Back to list</button>
        <PersonPane selected={selected} day={day} navigate={navigate} />
      </div>
    </div>
  );
}

function pill(active: boolean): React.CSSProperties {
  return {
    fontFamily: F.body, fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2,
    padding: "8px 14px", borderRadius: 999, cursor: "pointer",
    background: active ? C.sageDk : "#fff", color: active ? "#fff" : C.inkSoft,
    border: `1.5px solid ${active ? C.sageDk : C.hairline}`,
  };
}
